import { VerticalOption } from '@amzn/aet-galactus-kit';
import Alert from '@amzn/meridian/alert';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Link from '@amzn/meridian/link';
import Loader from '@amzn/meridian/loader';
import Responsive from '@amzn/meridian/responsive';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';
import Text from '@amzn/meridian/text';
import moment from 'moment';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router';
import DataSource from '../../../../api';
import { StateContext } from '../../../../GlobalStateStore';
import {
  EmptyHMDGoal,
  EmptyTeam,
  EmptyTeamProfile,
  Metric,
  Team as TeamType,
  TeamProfile,
  WeeklySummaryCoachingStatus,
} from '../../../../Types';
import CoachingNote from '../../../../Types/CoachingNote';
import DataState from '../../../../Types/DataState';
import {
  getCurrentWeek,
  weeksList,
  getDateRangeFromWeek,
} from '../../../../Util';
import {
  Feature,
  getAlertContentFromConfig,
  getNotificationContentFromConfig,
  isFeatureEnabled,
} from '../../../../config/FeaturesConfig';
import {
  EPOCH_START,
  FILTER_BAR_ID,
  FILTER_BAR_HEIGHT_THRESHOLD,
} from '../../../../Util/GlobalConstants';
import useCustomTracking from '../../../../Util/useCustomTracking';
import {
  EmptyFilters,
  EmptyLighthouse,
  isTmCheck,
  getTeam,
  getTeamIsTM,
  TeamTableSortMethod,
  isValidDateRange,
  getDefaultDateRange,
  refreshFilters,
} from '../../../Dashboard/DashboardComponents/LighthouseMetrics/helper';
import {
  LighthouseFilters,
  LighthouseMetrics,
  TimeOption,
} from '../../../Dashboard/DashboardComponents/LighthouseMetrics/types';
import UserBadge from '../../../Dashboard/DashboardComponents/UserBadge';
import CustomerComments from '../../../Dashboard/DashboardTables/CustomerComments';
import TeamPermissions from '../../TeamPermissions';
import DirectReports from './DirectReports';
import Summary from './Summary';
import TeamProfileContainer from './TeamProfileContainer';
import Sort from './LighthouseSort';
import LighthouseFilterBy from './LighthouseFilterBy';
import Page from '../../../../Types/Page';
import Filter from '../../FilterComponent/Filter';
import {
  convertToVerticalOption,
  decideVerticalOption,
} from '../../../../Util/Vertical';
import { checkGoUser } from '../../../../Util/User';

const TRAILING_DAYS = 'Trailing 7 days';

const Team: React.FC = (): React.ReactElement => {
  const apiNaws = DataSource.getDataFeed('NAWS');
  const legacyExperience = !isFeatureEnabled(Feature.LIGHTHOUSE);
  const newCoachingEnabled = isFeatureEnabled(Feature.NEW_COACHING);
  const enableProfanityFilter = isFeatureEnabled(Feature.PROFANITY_FILTER);
  const enableRAPRemovalAndCCXUpdate = isFeatureEnabled(
    Feature.ENABLE_RAP_REMOVAL_AND_CCX_UPDATE
  );
  // NOTE - Use this equivalent restriction flag as same as ARC
  const restrictionView = isFeatureEnabled(Feature.TREND_METRICS);
  let { userId } = useParams() as { userId: string };

  const globalState = useContext(StateContext);
  const { inspireOptions } = globalState.state;
  const { userID, cscEntityPrefix, enableCesComments, manageCoachingNotes } =
    inspireOptions.data;
  userId = userId || userID;

  const [showWeeklyCoachingStatus, setShowWeeklyCoachingStatus] =
    useState(true);
  const [team, setTeam] = useState<TeamType>(EmptyTeam);
  const [teamState, setTeamState] = useState(DataState.LOADING);

  // @FIXME: use global state
  const [profile, setTeamProfile] = useState<TeamProfile>(EmptyTeamProfile);
  const [profileState, setTeamProfileState] = useState(DataState.LOADING);

  const [selectedAssociate, setSelectedAssociate] = useState('');

  const [customerComments, setCustomerComments] = useState([]);
  const [customerCommentsState, setCustomerCommentsState] = useState(
    DataState.LOADING
  );

  const teamNotificationEnabled = isFeatureEnabled(Feature.TEAM_NOTIFICATION);
  const teamAlertEnabled = isFeatureEnabled(Feature.TEAM_ALERT);
  const achtEnabled = isFeatureEnabled(Feature.ACHT_FLAG);
  const concessionEnabled = isFeatureEnabled(Feature.CONCESSION_RATE_METRIC);

  const [weeklySummaryCoachingStatus, setWeeklySummaryCoachingStatus] =
    useState<WeeklySummaryCoachingStatus[]>([]);

  const [
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    weeklySummaryCoachingStatusLoadingState,
    setWeeklySummaryCoachingStatusLoadingState,
  ] = useState(DataState.LOADING);

  // Default Vertical
  const [LHDefaultVertical, setLHDefaultVertical] = useState('');
  const [LHDefaultVerticalState, setLHDefaultVerticalState] = useState(
    DataState.LOADING
  );

  // Lighthouse Filters
  const [lighthouseFilters, setLighthouseFilters] =
    useState<LighthouseFilters>(EmptyFilters);
  const [lighthouseFiltersState, setLighthouseFiltersState] = useState(
    DataState.LOADING
  );

  // Check for Vertical is in Vertical Options or not
  const [checkOptions, setCheckOptions] = useState(false);
  const [checkState, setCheckState] = useState(DataState.LOADING);

  // Team Filter
  const [verticalValue, setVerticalValue] = useState('');
  const [filterVerticalValue, setFilterVerticalValue] = useState('');
  const weekOptions = [TRAILING_DAYS, ...weeksList()];
  const [weekValue, setWeekValue] = useState(weekOptions[0]);
  const [skillValue, setSkillValue] = useState('ALL');
  const [channelValue, setChannelValue] = useState('ALL');
  const [segmentValue, setSegmentValue] = useState('ALL');
  const [marketplaceValue, setMarketplaceValue] = useState('ALL');
  const [skillOptions, setSkillOptions] = useState<string[]>([]);
  const [segmentOptions, setSegmentOptions] = useState<string[]>([]);
  const [verticalOptions, setVerticalOptions] = useState<string[]>([]);
  const [channelOptions, setChannelOptions] = useState<string[]>([]);
  const [marketplaceOptions, setMarketplaceOptions] = useState<string[]>([]);

  // Custom Date Feature: defualt set to recent 7 days
  const [customDate, setCustomDate] = useState(false);
  const defaultDateRange = getDefaultDateRange();
  const [dateRange, setDateRange] = useState(defaultDateRange);
  const [startDate, setStartDate] = useState(defaultDateRange[0]);
  const [endDate, setEndDate] = useState(defaultDateRange[1]);
  const [shortFilterBy, setShortFilterBy] = useState(false);

  // Summary Table - Lighthouse Metrics
  const [lighthouseMetrics, setLighthouseMetrics] =
    useState<LighthouseMetrics>(EmptyLighthouse);
  const [lighthouseMetricsState, setLighthouseMetricsState] = useState(
    DataState.LOADING
  );

  // contact listening
  const [contactHandlerId, setContactHandlerId] = useState('');

  // DirectReports Table - Team Lighthouse Metrics
  const [teamLHMetrics, setTeamLHMetrics] = useState<LighthouseMetrics[]>([]);
  const [teamLHMetricsState, setTeamLHMetricsState] = useState(
    DataState.LOADING
  );

  const [latestMetricPublishDate, setLatestMetricPublishDate] =
    useState(EPOCH_START);
  const [syncTimeChangeCount, setSyncTimeChangeCount] = useState(0);

  const updateTeamWithProfile = (prof: TeamProfile) => {
    // fill the shared profile table

    const availableKeys: string[] = [];
    if (inspireOptions.data.manageCoachingNotes) {
      availableKeys.push(TeamPermissions.coachingNotes);
    }

    const teamCopy = new TeamType({
      keys: availableKeys,
      metrics: [],
      summaryMetrics: [],
    });
    prof.reportingTeam.forEach(member => {
      const coachingNote = new CoachingNote(member.relatedUser.userID);
      const metric = new Metric({
        coachingNotes: [coachingNote],
        hmdGoal: EmptyHMDGoal,
      });
      teamCopy.metrics.push({
        user: member.relatedUser,
        metrics: metric,
        tm: member.isManager,
      });
    });
    setTeam(teamCopy);
  };

  const filterTruncate = {
    '500px': '100%',
    '860px': '50%',
    '1240px': '25%',
  };

  const isGoUser = checkGoUser(profile.user.owner);

  useEffect(() => {
    if (filterVerticalValue === '') return;
    setVerticalValue(
      decideVerticalOption(
        filterVerticalValue || LHDefaultVertical,
        isGoUser,
        enableRAPRemovalAndCCXUpdate
      )
    );
  }, [
    filterVerticalValue,
    isGoUser,
    LHDefaultVertical,
    enableRAPRemovalAndCCXUpdate,
  ]);

  useEffect(() => {
    // update args used for qureying if and only if the selected date range is valid
    if (isValidDateRange(dateRange[0], dateRange[1])) {
      setStartDate(dateRange[0]);
      setEndDate(dateRange[1]);
    }
  }, [dateRange]);

  useEffect(() => {
    // reset all filters (except vertical) to default every time user toggles the custom date switch
    if (customDate) {
      setDateRange(defaultDateRange);
      setStartDate(defaultDateRange[0]);
      setEndDate(defaultDateRange[1]);
    } else {
      setWeekValue(weekOptions[0]);
    }
    setSkillValue('ALL');
    setChannelValue('ALL');
    setSegmentValue('ALL');
    setMarketplaceValue('ALL');
  }, [customDate]);

  useEffect(() => {
    if (inspireOptions.state !== DataState.LOADED) {
      return;
    }

    if (weekValue && userId) {
      const getTeamProfileDataQuery = apiNaws.prepareTeamProfileDataQuery(
        userId,
        cscEntityPrefix
      );
      apiNaws.getTeamProfileData(
        getTeamProfileDataQuery,
        (prof: TeamProfile) => {
          updateTeamWithProfile(prof);
          setTeamProfile(prof);
        },
        (state: any) => {
          setTeamState(state);
          setTeamProfileState(state);
        }
      );
    }
    if (weekValue === TRAILING_DAYS || weekValue === getCurrentWeek()) {
      setShowWeeklyCoachingStatus(true);
    } else {
      setShowWeeklyCoachingStatus(false);
    }
  }, [inspireOptions.state]);

  // Default Vertical Integration
  useEffect(() => {
    if (profile.user.userID === '' || profileState === DataState.LOADING) {
      return;
    }
    const queryVariables = apiNaws.prepareLighthouseDefaultVerticalQuery(
      profile.user.userID,
      cscEntityPrefix
    );
    apiNaws.getDefaultVertical(
      queryVariables,
      setLHDefaultVertical,
      setLHDefaultVerticalState
    );
  }, [weekValue, profile, profileState, customDate, startDate, endDate]);

  useEffect(() => associateSelectClick(), [weekValue]);

  useEffect(() => {
    if (profile.user.userID === '' || profileState === DataState.LOADING) {
      return;
    }

    const isProfileTm = isTmCheck(profile);
    // Initialize filter Options to Avoid Race Condition
    if (lighthouseFilters !== EmptyFilters) {
      setLighthouseFilters(EmptyFilters);
      setLighthouseFiltersState(DataState.LOADING);
    }

    if (
      customDate &&
      startDate &&
      startDate != '' &&
      endDate &&
      endDate != ''
    ) {
      // custom date range
      const queryVariablesCustomFilters =
        apiNaws.prepareLighthouseCustomFiltersQuery(
          profile.user.userID,
          cscEntityPrefix,
          isProfileTm,
          startDate,
          endDate,
          getTeam(profile),
          getTeamIsTM(profile)
        );
      apiNaws.getLighthouseCustomFilters(
        queryVariablesCustomFilters,
        setLighthouseFilters,
        setLighthouseFiltersState
      );
    } else {
      // trailing 7 days or weekly
      const queryVariablesFilters = apiNaws.prepareLighthouseFiltersQuery(
        profile.user.userID,
        cscEntityPrefix,
        isProfileTm,
        weekValue,
        weekValue === TRAILING_DAYS ? TimeOption.DAILY : TimeOption.WEEKLY,
        getTeam(profile),
        getTeamIsTM(profile)
      );

      apiNaws.getLighthouseFilters(
        queryVariablesFilters,
        setLighthouseFilters,
        setLighthouseFiltersState
      );
    }
  }, [
    weekValue,
    profile.user.userID,
    profileState,
    syncTimeChangeCount,
    customDate,
    startDate,
    endDate,
  ]);

  // Handle the case for Vertical Options and Default Vertical
  useEffect(() => {
    if (
      LHDefaultVerticalState === DataState.LOADING ||
      lighthouseFiltersState === DataState.LOADING
    )
      return;

    if (legacyExperience) {
      setFilterVerticalValue(VerticalOption.Total_CSC);
      return;
    }

    // Check for Default Vertical in Filter Options or not
    if (
      LHDefaultVertical === null ||
      !lighthouseFilters.verticals.includes(LHDefaultVertical)
    ) {
      setFilterVerticalValue(VerticalOption.Total);
    } else {
      setFilterVerticalValue(convertToVerticalOption(LHDefaultVertical));
    }
  }, [lighthouseFiltersState, LHDefaultVerticalState]);

  useEffect(() => {
    if (
      LHDefaultVerticalState === DataState.LOADING ||
      lighthouseFiltersState === DataState.LOADING
    )
      return;

    if (!LHDefaultVertical) {
      setCheckOptions(false);
      setCheckState(DataState.LOADED);
      return;
    }
    setCheckOptions(lighthouseFilters.verticals.includes(LHDefaultVertical));
    setCheckState(DataState.LOADED);
  }, [lighthouseFiltersState, LHDefaultVerticalState]);

  useEffect(() => {
    if (
      profile.user.userID === '' ||
      lighthouseFiltersState !== DataState.LOADED ||
      LHDefaultVerticalState !== DataState.LOADED
    ) {
      return;
    }

    setSkillOptions(lighthouseFilters.skills);
    setChannelOptions(lighthouseFilters.channels);
    setSegmentOptions(lighthouseFilters.segments);
    // ToDo - Remove question marks after the release
    setMarketplaceOptions(lighthouseFilters.marketplaces ?? []);

    /**
     * Display Total as Aggregated - Make new array in the Order, that Total at first index
     */
    const verticalParam = lighthouseFilters.verticals;

    const tempVertical = ['Total', ...verticalParam];
    const sortedVertical = [...new Set(tempVertical)]; // Total is not default vertical - Regard it as Aggregated

    setVerticalOptions(sortedVertical);
  }, [lighthouseFiltersState, LHDefaultVerticalState, profile.user.userID]);

  // Reset check value to false and state to Loaded - since Default Check is used for the first time
  useEffect(() => {
    setCheckOptions(false);
    setCheckState(DataState.LOADED);
  }, [
    verticalValue,
    skillValue,
    channelValue,
    segmentValue,
    marketplaceValue,
    startDate,
    endDate,
  ]);

  useEffect(() => {
    if (
      profile.user.userID === '' ||
      verticalValue === '' ||
      lighthouseFiltersState !== DataState.LOADED ||
      LHDefaultVerticalState !== DataState.LOADED ||
      checkState === DataState.LOADING
    ) {
      return;
    }

    /**
     * 1. If Default vertical does exist in the filterOptions, then we pass variable in our query
     * 2. If Default vertical doesn't exist in the filterOptions, then we pass Total in our query
     *    2.1 VerticalValue Assignment has been done in another useEffect Block and when it is passed
     *        this block, it has been assigned to 'Total'
     */
    let verticalValueFromDefaultVertical = legacyExperience
      ? VerticalOption.Total_CSC
      : decideVerticalOption(
          LHDefaultVertical,
          isGoUser,
          enableRAPRemovalAndCCXUpdate
        );

    let verticalValueForCommonCase = legacyExperience
      ? VerticalOption.Total_CSC
      : convertToVerticalOption(verticalValue);

    const isProfileTm = isTmCheck(profile);
    let timestamp;
    let timeOption;
    if (customDate && dateRange[0] != '' && dateRange[1] != '') {
      const queryVariables = apiNaws.prepareCustomLighthouseQuery(
        profile.user.userID,
        cscEntityPrefix,
        isProfileTm,
        checkOptions
          ? verticalValueFromDefaultVertical
          : verticalValueForCommonCase,
        channelValue,
        skillValue,
        segmentValue,
        marketplaceValue,
        startDate,
        endDate,
        [], // Metrics Array - For topLevels - we pass empty Array
        isProfileTm
          ? profile.reportingTeam
              .map(member => member.relatedUser)
              .filter(user => user.relationToUser === 'DIRECT_REPORT')
          : []
      );
      apiNaws.getCustomLighthouseMetrics(
        queryVariables,
        setLighthouseMetrics,
        setLighthouseMetricsState
      );
    } else {
      // trailing 7 days or weekly
      timestamp = weekValue;
      timeOption =
        weekValue === TRAILING_DAYS ? TimeOption.DAILY : TimeOption.WEEKLY;
      const queryVariables = apiNaws.prepareLighthouseQuery(
        profile.user.userID,
        cscEntityPrefix,
        isProfileTm,
        checkOptions
          ? verticalValueFromDefaultVertical
          : verticalValueForCommonCase,
        channelValue,
        skillValue,
        segmentValue,
        marketplaceValue,
        timestamp,
        timeOption,
        [], // Metrics Array - For topLevels - we pass empty Array
        isProfileTm
          ? profile.reportingTeam
              .map(member => member.relatedUser)
              .filter(user => user.relationToUser === 'DIRECT_REPORT')
          : []
      );
      apiNaws.getLighthouseMetrics(
        queryVariables,
        setLighthouseMetrics,
        setLighthouseMetricsState
      );
    }
    setCheckState(DataState.LOADING);
  }, [
    profile.user.userID,
    verticalValue,
    weekValue,
    skillValue,
    channelValue,
    checkState,
    segmentValue,
    marketplaceValue,
    LHDefaultVerticalState,
    lighthouseFiltersState,
    inspireOptions.state,
    customDate,
    startDate,
    endDate,
    dateRange,
  ]);

  // Direct Reports Table
  useEffect(() => {
    if (
      profile.user.userID === '' ||
      verticalValue === '' ||
      checkState === DataState.LOADING ||
      lighthouseFiltersState !== DataState.LOADED ||
      LHDefaultVerticalState !== DataState.LOADED
    ) {
      return;
    }

    let verticalValueFromDefaultVertical = legacyExperience
      ? VerticalOption.Total_CSC
      : decideVerticalOption(
          LHDefaultVertical,
          isGoUser,
          enableRAPRemovalAndCCXUpdate
        );

    let verticalValueForCommonCase = legacyExperience
      ? VerticalOption.Total_CSC
      : convertToVerticalOption(verticalValue);

    if (customDate && dateRange[0] != '' && dateRange[1] != '') {
      // date range provided
      const queryVariablesLM = apiNaws.prepareCustomTeamLighthouseQuery(
        profile.user.userID,
        cscEntityPrefix,
        checkOptions
          ? verticalValueFromDefaultVertical
          : verticalValueForCommonCase,
        channelValue,
        skillValue,
        segmentValue,
        marketplaceValue,
        startDate,
        endDate,
        profile.reportingTeam
      );
      apiNaws.getCustomTeamLighthouseMetrics(
        queryVariablesLM,
        (payload: LighthouseMetrics[]) => {
          setTeamLHMetrics(payload);
          refreshFilters(
            payload,
            setLatestMetricPublishDate,
            setSyncTimeChangeCount
          );
        },
        setTeamLHMetricsState
      );
    } else {
      // trailing 7 days or weekly
      const queryVariablesLM = apiNaws.prepareTeamLighthouseQuery(
        profile.user.userID,
        cscEntityPrefix,
        checkOptions
          ? verticalValueFromDefaultVertical
          : verticalValueForCommonCase,
        channelValue,
        skillValue,
        segmentValue,
        marketplaceValue,
        weekValue,
        weekValue === TRAILING_DAYS ? TimeOption.DAILY : TimeOption.WEEKLY,
        profile.reportingTeam
      );
      apiNaws.getTeamLighthouseMetrics(
        queryVariablesLM,
        (payload: LighthouseMetrics[]) => {
          setTeamLHMetrics(payload);
          refreshFilters(
            payload,
            setLatestMetricPublishDate,
            setSyncTimeChangeCount
          );
        },
        setTeamLHMetricsState
      );
    }
  }, [
    profile.user.userID,
    weekValue,
    skillValue,
    channelValue,
    verticalValue,
    marketplaceValue,
    checkState,
    inspireOptions.state,
    LHDefaultVerticalState,
    lighthouseFiltersState,
    customDate,
    startDate,
    endDate,
  ]);

  useEffect(() => {
    if (inspireOptions.state !== DataState.LOADED) {
      return;
    }

    setWeeklySummaryCoachingStatusLoadingState(DataState.LOADING);
    apiNaws.getWeeklySummaryCoachingStatus(
      { userID: `${userId}` },
      setWeeklySummaryCoachingStatus,
      setWeeklySummaryCoachingStatusLoadingState
    );
  }, [inspireOptions.state]);

  const [SelectAssociateTrackingTag, associateSelectClick] = useCustomTracking(
    'team-associate-select',
    'team-associate-select-slot',
    {
      'data-csa-c-type': 'select',
      'data-csa-c-selected-associate': selectedAssociate || '',
    }
  );

  useEffect(() => {
    if (team.metrics.length) {
      const associate = team.metrics[0];
      // eslint-disable-next-line no-shadow
      const { user } = associate;
      // eslint-disable-next-line no-shadow
      const { userID } = user;
      setSelectedAssociate(userID);
    }
  }, [teamState]);

  const setAssociate = (e: React.FormEvent<HTMLSelectElement>): void => {
    setSelectedAssociate(e.toString());
  };

  const setWeek = (e: React.FormEvent<HTMLSelectElement>): void => {
    setWeekValue(e.toString());
  };

  let { startDate: contactStartDate, endDate: contactEndDate } =
    getDateRangeFromWeek(weekValue);

  useEffect(() => {
    const { startDate, endDate } = getDateRangeFromWeek(weekValue);
    contactStartDate = startDate;
    contactEndDate = endDate;
  }, [weekValue]);

  useEffect(() => {
    if (profile.user.userID === '' || !selectedAssociate) return;
    if (weekValue && enableCesComments) {
      apiNaws.getCustomerCommentsData(
        apiNaws.prepareQueryForCustomerComment({
          selectedWeek: weekValue,
          userID: selectedAssociate,
          cscEntityPrefix,
          enableProfanityFilter,
        }),
        setCustomerComments,
        setCustomerCommentsState
      );
      setContactHandlerId(`${cscEntityPrefix}:${selectedAssociate}`);
    }
  }, [weekValue, selectedAssociate, profile.user.userID, inspireOptions.state]);

  const weekValueChanger = (week: any) => {
    setSkillValue('ALL');
    setChannelValue('ALL');
    setSegmentValue('ALL');
    setMarketplaceValue('ALL');
    setCustomDate(false);
    setDateRange(defaultDateRange);
    legacyExperience
      ? setFilterVerticalValue(VerticalOption.Total_CSC)
      : LHDefaultVertical === null
        ? setFilterVerticalValue(VerticalOption.Total)
        : setFilterVerticalValue(LHDefaultVertical);
    setWeek(week);
  };

  useEffect(() => {
    setSkillValue('ALL');
    setChannelValue('ALL');
    setSegmentValue('ALL');
    setMarketplaceValue('ALL');
  }, [verticalValue]);

  const teamNotifContent = getNotificationContentFromConfig(
    Feature.TEAM_NOTIFICATION
  );

  const teamAlertContent = getAlertContentFromConfig(Feature.TEAM_ALERT);
  const [sortingMethod, setSortingMethod] = useState(
    TeamTableSortMethod.METRIC_VALUE
  );

  const showLastUpdateForQCS =
    verticalValue === VerticalOption.Total ||
    verticalValue === VerticalOption.Consumer;

  useEffect(() => {
    let filterBar = document.getElementById(FILTER_BAR_ID);
    if (
      filterBar?.offsetHeight &&
      filterBar?.offsetHeight > FILTER_BAR_HEIGHT_THRESHOLD
    ) {
      // two line filter bar
      setShortFilterBy(true);
    } else {
      // one line filter bar
      setShortFilterBy(false);
    }
  }, [customDate, verticalValue]);

  const teamMetricTypes = useMemo(
    () =>
      new Set([
        ...teamLHMetrics.map(metrics => metrics.metricTypes ?? []).flat(),
        ...lighthouseMetrics.metricTypes,
      ]),
    [teamLHMetrics, lighthouseMetrics]
  );

  return (
    <Box>
      <Responsive
        query='min-width'
        props={{
          isBig: {
            default: false,
            '1080px': true,
          },
        }}
      >
        {({ isBig }): React.ReactElement => (
          <>
            {teamNotificationEnabled && teamNotifContent !== null && (
              <Column spacing='none 400 500'>
                <Box spacingInset='none none 500'>
                  <Alert>
                    <Text>
                      {teamNotifContent?.defaultContent}{' '}
                      {teamNotifContent?.linkHref !== undefined &&
                        teamNotifContent?.linkText !== undefined && (
                          <Link
                            href={teamNotifContent?.linkHref}
                            target='_blank'
                            data-csa-c-type='link'
                            data-csa-c-slot-id='team-notification-link-slot'
                            data-csa-c-content-id='team-notification-link'
                          >
                            {teamNotifContent?.linkText}
                          </Link>
                        )}
                    </Text>
                  </Alert>
                </Box>
              </Column>
            )}

            {teamAlertEnabled && teamAlertContent !== null && (
              <Column spacing='none 400 500'>
                <Box spacingInset='none none 500'>
                  <Alert type='warning'>
                    <Text>{teamAlertContent}</Text>
                  </Alert>
                </Box>
              </Column>
            )}

            {!newCoachingEnabled && (
              <Box spacingInset='none none 500'>
                <Alert>
                  <Text>
                    <FormattedMessage
                      id='cs_perfportal_italy_release_banner'
                      defaultMessage='You do not have access to the coaching form at the moment. You will have access to all features within Inspire as soon as we have the necessary regulatory approvals to launch.'
                    />
                  </Text>
                </Alert>
              </Box>
            )}

            {profileState === DataState.LOADING && (
              <div style={{ textAlign: 'center' }}>
                <Loader type='circular' />
              </div>
            )}

            {profileState === DataState.LOADED && (
              <TeamProfileContainer
                state={profileState}
                profile={profile}
                selectedWeek={weekValue}
                setWeek={setWeek}
                isBig={isBig}
                sourcePage={Page.TEAM}
              />
            )}

            {profileState === DataState.ERROR && (
              <Alert type='error'>
                <FormattedMessage
                  id='cs_perfportal_error_reason_1'
                  defaultMessage='Your credentials may be incorrect or inadequate.'
                />
              </Alert>
            )}

            <Box spacingInset='none none 400' data-cy='team-performance'>
              <Heading level={1} type='h400'>
                <FormattedMessage
                  id='cs_perfportal_team_performance'
                  defaultMessage='Team Performance'
                />
              </Heading>
              {showLastUpdateForQCS && (
                <Text>
                  <FormattedMessage
                    id='cs_perfportal_last_updated'
                    defaultMessage='Last updated:'
                  />{' '}
                  {latestMetricPublishDate !== EPOCH_START
                    ? moment
                        .utc(latestMetricPublishDate)
                        .format('HH:mm YYYY-MM-DD')
                    : '-'}
                </Text>
              )}
            </Box>

            <Box type='fill' spacingInset='400'>
              {(lighthouseFiltersState === DataState.LOADING ||
                LHDefaultVerticalState === DataState.LOADING) && (
                <div style={{ textAlign: 'center' }}>
                  <Loader type='circular' size='large' />
                </div>
              )}
              {lighthouseFiltersState === DataState.LOADED &&
                LHDefaultVerticalState === DataState.LOADED && (
                  <Row
                    width='100%'
                    wrap='down'
                    alignmentVertical='center'
                    alignmentHorizontal='justify'
                  >
                    <div id={FILTER_BAR_ID}>
                      <Row wrap='down' alignmentVertical='top'>
                        <LighthouseFilterBy
                          shortFilterBy={shortFilterBy}
                          customDate={customDate}
                          page={Page.TEAM}
                        />
                        <Filter
                          verticalValue={filterVerticalValue}
                          weekValue={weekValue}
                          skillValue={skillValue}
                          channelValue={channelValue}
                          verticalOptions={verticalOptions}
                          skillOptions={skillOptions}
                          channelOptions={channelOptions}
                          filterTruncate={filterTruncate}
                          weekOptions={weekOptions}
                          customDate={customDate}
                          dateRange={dateRange}
                          setVerticalValue={setFilterVerticalValue}
                          setSkillValue={setSkillValue}
                          setChannelValue={setChannelValue}
                          weekValueChanger={weekValueChanger}
                          segmentOptions={segmentOptions}
                          segmentValue={segmentValue}
                          setSegmentValue={setSegmentValue}
                          setCustomDate={setCustomDate}
                          setDateRange={setDateRange}
                          page={Page.TEAM}
                          marketplaceValue={marketplaceValue}
                          marketplaceOptions={marketplaceOptions}
                          setMarketplaceValue={setMarketplaceValue}
                        />
                      </Row>
                    </div>
                  </Row>
                )}
              {(lighthouseFiltersState === DataState.ERROR ||
                LHDefaultVerticalState === DataState.ERROR) && (
                <Alert type='error'>
                  <FormattedMessage
                    id='cs_perfportal_error_refresh_page'
                    defaultMessage='Something went wrong. Please try refreshing the page. If the problem persists, please submit a SIM ticket for us to troubleshoot.'
                  />
                </Alert>
              )}
            </Box>

            {(teamState === DataState.LOADING ||
              lighthouseFiltersState === DataState.LOADING ||
              LHDefaultVerticalState === DataState.LOADING ||
              lighthouseMetricsState === DataState.LOADING) && (
              <div style={{ textAlign: 'center' }}>
                <Loader type='circular' />
              </div>
            )}
            {teamState === DataState.LOADED &&
              lighthouseFiltersState === DataState.LOADED &&
              LHDefaultVerticalState === DataState.LOADED &&
              lighthouseMetricsState === DataState.LOADED && (
                <Row width='100%' spacingInset='none' spacing='none'>
                  <Summary
                    keys={team.keys}
                    vertical={convertToVerticalOption(verticalValue)}
                    lighthouseMetrics={lighthouseMetrics}
                    achtEnabled={achtEnabled}
                    concessionEnabled={concessionEnabled}
                    manageCoachingNotes={manageCoachingNotes}
                    teamMetricTypes={teamMetricTypes}
                  />
                </Row>
              )}
            {(teamState === DataState.ERROR ||
              lighthouseFiltersState === DataState.ERROR ||
              LHDefaultVerticalState === DataState.ERROR ||
              lighthouseMetricsState === DataState.ERROR) && (
              <>
                <Row width='100%' spacingInset='400 none none' spacing='none'>
                  <Alert type='error'>
                    <FormattedMessage
                      id='cs_perfportal_error_refresh_page'
                      defaultMessage='Something went wrong. Please try refreshing the page. If the problem persists, please submit a SIM ticket for us to troubleshoot.'
                    />
                  </Alert>
                </Row>
              </>
            )}

            {(teamState === DataState.LOADING ||
              LHDefaultVerticalState === DataState.LOADING ||
              teamLHMetricsState === DataState.LOADING ||
              lighthouseFiltersState === DataState.LOADING) && (
              <div style={{ textAlign: 'center' }}>
                <Loader type='circular' />
              </div>
            )}
            {teamState === DataState.LOADED &&
              lighthouseFiltersState === DataState.LOADED &&
              LHDefaultVerticalState === DataState.LOADED &&
              teamLHMetricsState === DataState.LOADED && (
                <>
                  <Box type='fill' spacingInset='400'>
                    <Row
                      width='100%'
                      wrap='down'
                      alignmentVertical='center'
                      alignmentHorizontal='justify'
                    >
                      <Row wrap='down'>
                        <Text>
                          <FormattedMessage
                            id='cs_perfportal_sort_metric_by'
                            defaultMessage='Sort Metric by'
                          />
                        </Text>
                        <Sort
                          sortMethod={sortingMethod}
                          setSortMethod={setSortingMethod}
                          sourcePage={Page.TEAM}
                        />
                      </Row>
                    </Row>
                  </Box>
                  <Row width='100%' spacingInset='none' spacing='none'>
                    <DirectReports
                      cscEntityPrefix={cscEntityPrefix}
                      vertical={convertToVerticalOption(verticalValue)}
                      channelValue={channelValue}
                      skillValue={skillValue}
                      segmentValue={segmentValue}
                      marketplaceValue={marketplaceValue}
                      keys={team.keys}
                      week={weekValue}
                      profile={profile}
                      weeklySummaryCoachingStatus={weeklySummaryCoachingStatus}
                      showWeeklyCoachingStatus={showWeeklyCoachingStatus}
                      teamLHMetrics={teamLHMetrics}
                      achtEnabled={achtEnabled}
                      concessionEnabled={concessionEnabled}
                      manageCoachingNotes={manageCoachingNotes}
                      sortingMethod={sortingMethod}
                      sourcePage={Page.TEAM}
                      teamMetricTypes={teamMetricTypes}
                    />
                  </Row>
                </>
              )}

            {(teamState === DataState.ERROR ||
              lighthouseFiltersState === DataState.ERROR ||
              LHDefaultVerticalState === DataState.ERROR ||
              teamLHMetricsState === DataState.ERROR) && (
              <Row width='100%' spacingInset='none' spacing='none'>
                <Alert type='error'>
                  <FormattedMessage
                    id='cs_perfportal_error_refresh_page'
                    defaultMessage='Something went wrong. Please try refreshing the page. If the problem persists, please submit a SIM ticket for us to troubleshoot.'
                  />
                </Alert>
              </Row>
            )}

            {enableCesComments && (
              <>
                <Row
                  spacingInset='500 none none'
                  spacing='400'
                  widths={['75.87%', '24.13%']}
                >
                  <Box>
                    <Heading level={1} type='h400'>
                      <FormattedMessage
                        id='cs_perfportal_customerComments'
                        defaultMessage='Customer comments'
                        description='header of Customer Comments Table'
                      />
                    </Heading>
                  </Box>
                  <Box>
                    <SelectAssociateTrackingTag />
                    <Select
                      value={selectedAssociate}
                      onChange={setAssociate}
                      size='medium'
                    >
                      {team.metrics.map(w => {
                        return (
                          <SelectOption
                            key={w.user.userID}
                            value={w.user.userID}
                            label={w.user.displayName}
                            data-csa-c-type='select'
                            data-csa-c-slot-id='lighthouse-filters-select-username-customer-comments'
                            data-csa-c-content-id='lighthouse-filters-select-username-customer-comments'
                            data-csa-c-selected-value={w.user.userID}
                            data-csa-c-tag1='Team'
                            data-csa-c-tag2='Customer Comments'
                          >
                            {(): React.ReactElement => (
                              <>
                                <Row spacing='none' spacingInset='none'>
                                  <Box width='34.9px' height='36px'>
                                    <UserBadge
                                      username={w.user.userName}
                                      givenName={w.user.displayName}
                                      tooltip
                                    />
                                  </Box>
                                  <Text>{w.user.displayName}</Text>
                                </Row>
                              </>
                            )}
                          </SelectOption>
                        );
                      })}
                    </Select>
                  </Box>
                </Row>

                {customerCommentsState === DataState.LOADING && (
                  <div style={{ textAlign: 'center' }}>
                    <Loader type='circular' size='large' />
                  </div>
                )}

                {customerCommentsState === DataState.LOADED && (
                  <Row spacingInset='none' spacing='none'>
                    <CustomerComments
                      customerMessages={customerComments}
                      page={Page.TEAM}
                      contactStartDate={contactStartDate}
                      contactEndDate={contactEndDate}
                      contactHandlerId={contactHandlerId}
                    />
                  </Row>
                )}
                {customerCommentsState === DataState.ERROR && (
                  <Alert type='error'>
                    <FormattedMessage
                      id='cs_perfportal_error_reason_1'
                      defaultMessage='Your credentials may be incorrect or inadequate.'
                    />
                  </Alert>
                )}
              </>
            )}
          </>
        )}
      </Responsive>
    </Box>
  );
};

export default Team;
